import { supabase } from '../supabase';

// ── Team creation ──────────────────────────────────────────────────────
// Atomic server-side: creates the team and the creator's Team Admin row
// (and optionally Head Coach) together. The only live team-creation path.
export async function createTeam({ teamName, city, state, zipCode, season, gradeAgeGroup, organizationName, alsoHeadCoach }) {
  const { data, error } = await supabase.rpc('create_team_with_admin', {
    p_team_name: teamName,
    p_city: city,
    p_state: state,
    p_zip_code: zipCode,
    p_season: season,
    p_grade_age_group: gradeAgeGroup,
    p_organization_name: organizationName || null,
    p_also_head_coach: Boolean(alsoHeadCoach),
  });
  if (error) throw error;
  return data;
}

// ── Teams ──────────────────────────────────────────────────────────────

/** Teams where userId currently holds any active staff role (with those
 * roles attached), PLUS Teams where userId is an active guardian of an
 * athlete who currently has an ACTIVE roster spot — merged into one
 * list, one card per Team even when both relationships exist on the
 * same Team (a coach who is also a guardian of a rostered athlete on
 * their own team never gets a duplicate card). Guardian-only Teams get
 * roles: [] — guardians are never made team_staff, so TeamCard's
 * existing role-chip rendering naturally shows none for them.
 *
 * Deliberately does NOT query team_roster_requests at all — a pending,
 * declined, or cancelled request must never surface a Team here on its
 * own; team_athletes.status = 'active' (the real, current roster
 * relationship, 012) is the only source of truth. RLS already restricts
 * both queries below to rows the caller is actually allowed to see
 * ("guardians view own links" on guardian_athletes, 005; "roster
 * visibility" on team_athletes, 012 — which already grants a guardian of
 * the athlete read access regardless of the row's own status, so the
 * .eq('status','active') filter here is this function's own
 * "current membership only" business rule, not an RLS gap being papered
 * over) — an unrelated user's queries simply return no rows from either. */
export async function listMyTeams(userId) {
  const [staffResult, guardianLinksResult] = await Promise.all([
    supabase.from('team_staff').select('role, team:teams(*)').eq('user_id', userId).eq('status', 'active'),
    supabase.from('guardian_athletes').select('athlete_id').eq('guardian_user_id', userId).eq('status', 'active'),
  ]);
  if (staffResult.error) throw staffResult.error;
  if (guardianLinksResult.error) throw guardianLinksResult.error;

  const byTeam = {};
  (staffResult.data || []).forEach(row => {
    if (!row.team) return;
    const id = row.team.id;
    if (!byTeam[id]) byTeam[id] = { ...row.team, roles: [] };
    byTeam[id].roles.push(row.role);
  });

  const athleteIds = (guardianLinksResult.data || []).map(l => l.athlete_id);
  if (athleteIds.length > 0) {
    const { data: teamAthleteRows, error: teamAthleteError } = await supabase
      .from('team_athletes')
      .select('team_id, team:teams(*)')
      .in('athlete_id', athleteIds)
      .eq('status', 'active');
    if (teamAthleteError) throw teamAthleteError;
    (teamAthleteRows || []).forEach(row => {
      if (!row.team) return;
      const id = row.team.id;
      if (!byTeam[id]) byTeam[id] = { ...row.team, roles: [] };
    });
  }

  return Object.values(byTeam);
}

export async function getTeam(teamId) {
  const { data, error } = await supabase.from('teams').select('*').eq('id', teamId).single();
  if (error) throw error;
  return data;
}

export async function updateTeam(teamId, fields) {
  const { data, error } = await supabase.from('teams').update(fields).eq('id', teamId).select().single();
  if (error) throw error;
  return data;
}

// ── Staff ──────────────────────────────────────────────────────────────

/** Full staff list with names/emails, via the list_team_staff() RPC — a
 * direct client join to profiles would come back null for everyone but
 * yourself (profiles' own RLS restricts SELECT to your own row). */
export async function listTeamStaff(teamId) {
  const { data, error } = await supabase.rpc('list_team_staff', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

export async function addTeamStaff({ teamId, email, role }) {
  const { data, error } = await supabase.rpc('add_team_staff', {
    p_team_id: teamId,
    p_email: email,
    p_role: role,
  });
  if (error) throw error;
  return data;
}

export async function revokeTeamStaff(teamStaffId) {
  const { data, error } = await supabase.rpc('revoke_team_staff', { p_team_staff_id: teamStaffId });
  if (error) throw error;
  return data;
}
